import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Wrench, Car, Shield, ChevronRight, Key, Star, Quote, Cpu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import CarCard from "@/components/CarCard";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [cars, setCars] = useState([]);
  const [settings, setSettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carsRes, settingsRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/cars?disponible=true`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/reviews`)
        ]);
        setCars(carsRes.data.slice(0, 6));
        setSettings(settingsRes.data);
        setReviews(reviewsRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: Wrench,
      title: "Réparation",
      description: "Entretien et réparation tous véhicules toutes marques"
    },
    {
      icon: Car,
      title: "Vente",
      description: "Large choix de véhicules d'occasion garantis"
    },
    {
      icon: Shield,
      title: "Garantie",
      description: "Garantie 12 mois sur tous nos véhicules"
    },
    {
      icon: Key,
      title: "Serrurerie Auto",
      description: "Réparation et programmation de clés automobiles"
    },
    {
      icon: Cpu,
      title: "Électronique",
      description: "Diagnostic et réparation systèmes électroniques"
    }
  ];

  const galleryImages = [
    "https://images.unsplash.com/photo-1717068341263-33331ec8104c?w=600",
    "https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=600",
    "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=600",
    "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=600"
  ];

  const renderStars = (note) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < note ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getImageUrl(settings?.hero_image) || "https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=1920"}')`
          }}
        />
        {/* Darker overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight animate-fade-in-up">
              {(settings?.hero_titre || "VOTRE GARAGE DE CONFIANCE").split(" ").slice(0, -2).join(" ") || "VOTRE GARAGE"}<br />
              <span className="text-yellow-400">{(settings?.hero_titre || "VOTRE GARAGE DE CONFIANCE").split(" ").slice(-2).join(" ") || "DE CONFIANCE"}</span>
            </h1>
            <p className="mt-6 text-xl text-gray-200 max-w-xl animate-fade-in-up animation-delay-100">
              {settings?.hero_sous_titre || "Vente et réparation automobile à Crépy-en-Valois. Plus de 15 ans d'expertise à votre service."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-in-up animation-delay-200">
              <Link to="/voitures">
                <Button 
                  className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase tracking-wider text-lg px-8 py-6"
                  data-testid="hero-cta-voitures"
                >
                  {settings?.hero_btn1_text || "Voir nos voitures"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  className="bg-white/10 backdrop-blur text-white hover:bg-white/20 border border-white/30 text-lg px-8 py-6"
                  data-testid="hero-cta-rdv"
                >
                  {settings?.hero_btn2_text || "Nous contacter"}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="py-8 bg-gray-900" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {galleryImages.map((img, index) => (
              <div key={index} className="relative overflow-hidden group">
                <img 
                  src={img} 
                  alt={`Garage ${index + 1}`}
                  className="w-full h-40 md:h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/galerie" className="text-yellow-400 hover:text-yellow-300 text-sm font-medium inline-flex items-center gap-1">
              Voir toute la galerie <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-spacing bg-gray-50" data-testid="services-section">
        <div className="page-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {settings?.services_titre?.split(" ")[0] || "NOS"} <span className="text-yellow-500">{settings?.services_titre?.split(" ").slice(1).join(" ") || "SERVICES"}</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {settings?.services_description || "Une expertise complète pour tous vos besoins automobiles"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="service-card animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-yellow-100 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="link" className="text-yellow-600 hover:text-yellow-700">
                Découvrir tous nos services
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Serrurerie Auto CTA */}
      <section className="py-16 bg-gray-900" data-testid="serrurerie-section">
        <div className="page-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
                <Key className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Service Serrurerie Automobile</h3>
                <p className="text-gray-300">Clé perdue ? Clé cassée ? Programmation de télécommande ? Nous avons la solution !</p>
              </div>
            </div>
            <Link to="/contact">
              <Button className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase whitespace-nowrap">
                Nous contacter
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Cars Section */}
      <section className="section-spacing bg-white" data-testid="featured-cars-section">
        <div className="page-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                VÉHICULES <span className="text-yellow-500">EN VENTE</span>
              </h2>
              <p className="text-gray-600">
                Découvrez notre sélection de véhicules d'occasion
              </p>
            </div>
            <Link to="/voitures">
              <Button className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold uppercase">
                Voir tout
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cars.map((car, index) => (
                <div 
                  key={car.id} 
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CarCard car={car} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="section-spacing bg-gray-50" data-testid="reviews-section">
          <div className="page-container">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                AVIS <span className="text-yellow-500">CLIENTS</span>
              </h2>
              <p className="text-gray-600">Ce que nos clients disent de nous</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {reviews.map((review, index) => (
                <div 
                  key={review.id}
                  className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 relative animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Quote className="w-10 h-10 text-yellow-100 absolute top-6 right-6" />
                  <div className="flex gap-1 mb-4">
                    {renderStars(review.note)}
                  </div>
                  <p className="text-gray-600 mb-6 italic">"{review.commentaire}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <span className="text-yellow-600 font-bold">{review.nom.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{review.nom}</p>
                      {review.service && <p className="text-sm text-gray-500">{review.service}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-spacing bg-yellow-500" data-testid="cta-section">
        <div className="page-container text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            BESOIN D'UN RENDEZ-VOUS ?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8 text-lg">
            Notre équipe est à votre disposition pour tous vos besoins en entretien 
            et réparation automobile.
          </p>
          <a href={`tel:${settings?.telephone?.replace(/\s/g, '') || '0646634968'}`}>
            <Button 
              className="bg-white text-yellow-600 hover:bg-gray-100 font-bold uppercase text-lg px-10 py-6"
              data-testid="cta-rdv-button"
            >
              <Phone className="w-5 h-5 mr-2" />
              Appelez-nous !
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
